/**
 * @file Everything the operator can do to a running Signify fetch: insert what is ready, retry what
 * failed and apply the choices they made, cancel, or start a new list. Consumer:
 * `SignifyFetchJobProgress`.
 * Like the header, it subscribes to every part on purpose — its counts are the header's counts,
 * from the same `deriveSignifyFetchJobProgress` call — and it renders buttons rather than a table,
 * so one render per part write costs nothing.
 * Nothing is ever hidden: a control the state forbids is disabled, its reason is in its
 * `aria-label`, and the reasons in force are repeated in one line beneath the row.
 * Retry and the picks travel together because the callable takes them in one request and resets
 * every failed part whether picks are sent or not; two buttons would each do the other's work. A
 * running fetch does not disable it: the reason comes from `signifyFetchJobRetryDisabledReason`,
 * the same rule each failed row's own button obeys. `retrying` and `cancelling` disable their own
 * button and never the other's, because a retry is what resumes a job a cancel has just stopped.
 * A stalled fetch relabels Retry as the resume it actually is, since the retry enqueues whether or
 * not a part failed and is the only way back from a dispatch that died mid-run.
 */

import { FC, useEffect, useState } from 'react';
import { Button, Popconfirm, Switch, Typography } from 'antd';
import { useSnapshot } from 'valtio';
import FlexBox from '@/components/atoms/FlexBox';
import FlexCol from '@/components/atoms/FlexCol';
import type { SignifyFetchJob, SignifyFetchPart } from '#types';
import type { CollectionStore, DocumentStore } from '@/store';
import { deriveSignifyFetchJobProgress, signifyFetchJobRetryDisabledReason } from '../use/services';
import type { SignifyFetchJobInserting, SignifyFetchJobPicks } from '../index.types';

const { Text } = Typography;

const CLOCK_INTERVAL_MS = 5_000;

type SignifyFetchJobProgressActionsProps = {
  jobStore: DocumentStore<SignifyFetchJob>;
  partsStore: CollectionStore<SignifyFetchPart>;
  picks: SignifyFetchJobPicks;
  inserting: SignifyFetchJobInserting;
  saveRecord: boolean;
  canWriteRecords: boolean;
  disabled: boolean;
  retrying: boolean;
  cancelling: boolean;
  onSaveRecordChange: (checked: boolean) => void;
  onInsert: () => void;
  onRetry: () => void;
  onCancel: () => void;
  onNewList: () => void;
};

const SignifyFetchJobProgressActions: FC<SignifyFetchJobProgressActionsProps> = ({
  jobStore,
  partsStore,
  picks,
  inserting,
  saveRecord,
  canWriteRecords,
  disabled,
  retrying,
  cancelling,
  onSaveRecordChange,
  onInsert,
  onRetry,
  onCancel,
  onNewList,
}) => {
  const [now, setNow] = useState(Date.now);
  const jobSnap = useSnapshot(jobStore.state);
  const partsSnap = useSnapshot(partsStore.state);
  const picksSnap = useSnapshot(picks);
  const insertingSnap = useSnapshot(inserting);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), CLOCK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  const job = jobSnap.data;
  const parts = partsSnap.docIds.flatMap((partId) => {
    const entry = partsSnap.docs.get(partId);
    return entry ? [entry.data] : [];
  });
  const progress = deriveSignifyFetchJobProgress(
    job === null
      ? null
      : { phase: job.phase, heartbeatAt: job.heartbeatAt, concurrency: job.concurrency },
    parts,
    now,
  );

  const pickCount = Object.keys(picksSnap).length;
  const running = progress.status === 'queued' || progress.status === 'running' || progress.status === 'stalled';
  const insertInFlight = insertingSnap.phase !== 'idle';

  const insertDisabledReason = disabled
    ? 'You do not have permission to insert'
    : insertInFlight
      ? 'An insert is already running'
      : progress.ready === 0
        ? (progress.done === 0 ? 'No fetched parts to insert yet' : 'Every fetched part is inserted')
        : null;
  const insertLabel = insertingSnap.phase === 'preparing'
    ? `Preparing ${insertingSnap.done} of ${insertingSnap.total}…`
    : insertingSnap.phase === 'inserting'
      ? `Inserting ${insertingSnap.done} of ${insertingSnap.total}…`
      : `Insert ready parts (${progress.ready})`;

  const retryDisabledReason = signifyFetchJobRetryDisabledReason({
    retrying,
    retryable: progress.failed + pickCount,
    status: progress.status,
  });
  const stalled = progress.status === 'stalled';
  const choices = `${pickCount} ${pickCount === 1 ? 'choice' : 'choices'}`;
  const retryLabel = progress.failed > 0 && pickCount > 0
    ? `Retry failed (${progress.failed}) · apply ${choices}`
    : progress.failed > 0
      ? (stalled ? `Retry failed (${progress.failed}) and resume` : `Retry failed (${progress.failed})`)
      : pickCount > 0
        ? `Apply ${choices}`
        : stalled
          ? 'Resume the stalled fetch'
          : 'Retry failed parts';

  const cancelDisabledReason = cancelling
    ? 'A cancel is already running'
    : progress.status === 'completed'
      ? 'The fetch has finished'
      : progress.status === 'cancelled'
        ? 'The fetch was cancelled'
        : null;

  const newListDisabledReason = running ? 'Finish or cancel the running fetch first' : null;
  const recordDisabledReason = canWriteRecords ? null : 'You cannot write records';

  const reasons = [
    insertDisabledReason,
    recordDisabledReason,
    retryDisabledReason,
    cancelDisabledReason,
    newListDisabledReason,
  ].filter((reason): reason is string => reason !== null);

  return (
    <FlexCol gap={5}>
      <FlexBox justifyStart gap={10} wrap>
        <Button
          type="primary"
          aria-label={
            insertDisabledReason
              ? `signify-fetch-job-insert (${insertDisabledReason})`
              : 'signify-fetch-job-insert'
          }
          disabled={!!insertDisabledReason}
          loading={insertInFlight}
          onClick={onInsert}
        >
          {insertLabel}
        </Button>

        <FlexBox justifyStart gap={5} noGrow>
          <Switch
            size="small"
            checked={saveRecord}
            disabled={!!recordDisabledReason}
            onChange={onSaveRecordChange}
            aria-label={
              recordDisabledReason
                ? `signify-fetch-job-save-record-toggle (${recordDisabledReason})`
                : 'signify-fetch-job-save-record-toggle'
            }
          />
          <Text type="secondary">Also save as record</Text>
        </FlexBox>

        <Button
          aria-label={
            retryDisabledReason
              ? `signify-fetch-job-retry-failed (${retryDisabledReason})`
              : 'signify-fetch-job-retry-failed'
          }
          disabled={!!retryDisabledReason}
          onClick={onRetry}
        >
          {retryLabel}
        </Button>

        <Button
          aria-label={
            cancelDisabledReason
              ? `signify-fetch-job-cancel (${cancelDisabledReason})`
              : 'signify-fetch-job-cancel'
          }
          disabled={!!cancelDisabledReason}
          onClick={onCancel}
        >
          Cancel the fetch
        </Button>

        <Popconfirm
          title={`${progress.ready} fetched ${progress.ready === 1 ? 'part is' : 'parts are'} not inserted. Start a new list anyway?`}
          okText="Start a new list"
          cancelText="Keep this list"
          disabled={!!newListDisabledReason || progress.ready === 0}
          onConfirm={onNewList}
        >
          <Button
            aria-label={
              newListDisabledReason
                ? `signify-fetch-job-new-list (${newListDisabledReason})`
                : 'signify-fetch-job-new-list'
            }
            disabled={!!newListDisabledReason}
            onClick={() => {
              if (progress.ready === 0) onNewList();
            }}
          >
            New list
          </Button>
        </Popconfirm>
      </FlexBox>

      {reasons.length > 0 ? <Text type="secondary">{reasons.join(' · ')}</Text> : null}
    </FlexCol>
  );
};

export default SignifyFetchJobProgressActions;
